// Admin review of public application submissions.
// Lists rows by status, opens a single modal for inspection, supports
// approve (with organization picker) and reject (with reason). All mutations
// are platform-admin gated server-side.
import { api } from '../api';
import { initAdminShell, escapeHtml, showError, hideError, confirmModal, setButtonBusy } from './shell';
import { type PagedResult } from './pagination';
import { t } from '../i18n/i18n';

interface Application {
  id: number;
  organisation: string;
  name: string;
  email: string;
  phone: string | null;
  message: string | null;
  status: 'pending' | 'approved' | 'rejected' | string;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  approvedOrganizationId: string | null;
  createdUserId: string | null;
}

interface Organization {
  id: string;
  name: string;
}

const PAGE_SIZE = 50;

(async () => {
  const me = await initAdminShell('applications').catch(() => null);
  if (!me) return;

  const tbody = document.getElementById('pa-tbody');
  const filterSelect = document.getElementById('pa-filter-status') as HTMLSelectElement | null;
  if (!tbody) return;

  let organizations: Organization[] = [];
  try {
    const client = await api();
    const { data } = await client.get<Organization[]>('/api/admin/organizations');
    organizations = data;
  } catch {
    // Picker stays empty - approve will fail with a clear backend error.
  }

  let statusFilter = 'pending';

  async function load(): Promise<void> {
    hideError('pa-error');
    try {
      const client = await api();
      const params: Record<string, string | number> = { page: 1, pageSize: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const { data } = await client.get<PagedResult<Application>>('/api/admin/applications', { params });
      render(data.items);
    } catch (err) {
      tbody!.innerHTML = '';
      showError('pa-error', err instanceof Error ? err.message : t('admin.applications.err_load'));
    }
  }

  function render(rows: Application[]): void {
    if (!tbody) return;
    if (rows.length === 0) {
      tbody.innerHTML = `<tr><td colspan="6" class="req-empty">${escapeHtml(t('admin.applications.empty'))}</td></tr>`;
      return;
    }
    tbody.innerHTML = rows.map((r) => {
      const statusLabel = t(`admin.applications.status_${r.status}`);
      const statusClass = `status-pill--${r.status === 'pending' ? 'new' : r.status === 'approved' ? 'approved' : 'rejected'}`;
      const reviewLabel = t('admin.applications.review_action');
      return `
        <tr data-id="${r.id}" style="cursor:pointer;">
          <td><div class="req-table__project">${escapeHtml(r.organisation)}</div></td>
          <td>${escapeHtml(r.name)}</td>
          <td>${escapeHtml(r.email)}</td>
          <td><span class="status-pill ${statusClass}">${escapeHtml(statusLabel)}</span></td>
          <td class="req-table__date">${escapeHtml(formatDate(r.createdAt))}</td>
          <td class="row-actions">
            <button type="button" class="row-action" data-action="review" data-id="${r.id}">
              ${escapeHtml(reviewLabel)}
            </button>
          </td>
        </tr>
      `;
    }).join('');

    tbody.querySelectorAll<HTMLElement>('tr[data-id]').forEach((tr) => {
      tr.addEventListener('click', () => openModal(Number(tr.dataset.id)));
    });
  }

  filterSelect?.addEventListener('change', () => {
    statusFilter = filterSelect.value;
    void load();
  });

  // ---- Modal ----

  const overlay   = document.getElementById('pa-modal-overlay') as HTMLElement | null;
  const modalTitle= document.getElementById('pa-modal-title') as HTMLElement | null;
  const modalBody = document.getElementById('pa-modal-body') as HTMLElement | null;
  const modalFoot = document.getElementById('pa-modal-foot') as HTMLElement | null;

  function closeModal(): void {
    overlay?.classList.remove('is-open');
    overlay?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.getElementById('pa-modal-close')?.addEventListener('click', closeModal);
  overlay?.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay?.classList.contains('is-open')) closeModal();
  });

  async function openModal(id: number): Promise<void> {
    if (!overlay || !modalBody || !modalFoot || !modalTitle) return;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    modalBody.innerHTML = `<p style="color:var(--sk-muted);">${escapeHtml(t('admin.actions.loading'))}</p>`;
    modalFoot.innerHTML = '';
    try {
      const client = await api();
      const { data } = await client.get<Application>(`/api/admin/applications/${id}`);
      modalTitle.textContent = data.organisation;
      modalBody.innerHTML = renderModalBody(data);
      modalFoot.innerHTML = renderModalFoot(data);
      bindModalActions(data);
    } catch (err) {
      modalBody.innerHTML = `<p style="color:var(--sk-status-occupied);">${escapeHtml(err instanceof Error ? err.message : t('admin.applications.err_load'))}</p>`;
    }
  }

  function renderModalBody(a: Application): string {
    const cell = (label: string, value: string) => `
      <div class="detail-grid__cell">
        <div class="detail-grid__label">${escapeHtml(label)}</div>
        <div class="detail-grid__value">${value}</div>
      </div>`;
    const statusLabel = t(`admin.applications.status_${a.status}`);
    const statusClass = `status-pill--${a.status === 'pending' ? 'new' : a.status === 'approved' ? 'approved' : 'rejected'}`;

    let reviewBlock = '';
    if (a.status !== 'pending') {
      const organizationName = a.approvedOrganizationId
        ? organizations.find((o) => o.id === a.approvedOrganizationId)?.name ?? a.approvedOrganizationId
        : null;
      reviewBlock = `
        <div class="req-drawer__section" style="margin-top:18px;">
          <h4>${escapeHtml(t('admin.applications.section_review'))}</h4>
          <div class="detail-grid">
            ${cell(t('admin.applications.reviewed_by'), escapeHtml(a.reviewedBy ?? '-'))}
            ${cell(t('admin.applications.reviewed_at'), escapeHtml(a.reviewedAt ? formatDateTime(a.reviewedAt) : '-'))}
            ${a.status === 'approved' && organizationName ? cell(t('admin.applications.approved_organization'), escapeHtml(organizationName)) : ''}
          </div>
          ${a.reviewNote ? `<p style="margin-top:10px;font-size:13px;color:var(--sk-navy-800);background:var(--sk-surface);padding:10px 14px;border-left:3px solid var(--sk-blue-500);">${escapeHtml(a.reviewNote)}</p>` : ''}
        </div>
      `;
    }

    return `
      <div style="margin-bottom:14px;">
        <span class="status-pill ${statusClass}">${escapeHtml(statusLabel)}</span>
      </div>
      <div class="detail-grid">
        ${cell(t('admin.applications.col.organisation'), escapeHtml(a.organisation))}
        ${cell(t('admin.applications.col.contact'), escapeHtml(a.name))}
        ${cell(t('admin.applications.col.email'), `<a href="mailto:${escapeHtml(a.email)}">${escapeHtml(a.email)}</a>`)}
        ${cell(t('admin.applications.col.phone'), escapeHtml(a.phone ?? '-'))}
        ${cell(t('admin.applications.col.submitted'), escapeHtml(formatDateTime(a.createdAt)))}
      </div>
      ${a.message ? `
        <div style="margin-top:14px;">
          <div class="detail-grid__label">${escapeHtml(t('admin.applications.col.message'))}</div>
          <p style="margin-top:4px;font-size:13.5px;line-height:1.55;background:var(--sk-surface);padding:10px 14px;border:1px solid var(--sk-border);">${escapeHtml(a.message)}</p>
        </div>
      ` : ''}
      ${reviewBlock}
      ${a.status === 'pending' ? renderApproveForm() : ''}
    `;
  }

  function renderApproveForm(): string {
    return `
      <div class="req-drawer__section" style="margin-top:20px;">
        <h4>${escapeHtml(t('admin.applications.section_approve'))}</h4>
        <p style="font-size:12.5px;color:var(--sk-muted);margin:0 0 10px;">${escapeHtml(t('admin.applications.approve_explainer'))}</p>
        <div class="form-field">
          <label for="pa-approve-note">${escapeHtml(t('admin.applications.label_note'))}</label>
          <textarea id="pa-approve-note" rows="2" placeholder="${escapeHtml(t('admin.applications.note_placeholder'))}"></textarea>
        </div>
      </div>
      <div class="req-drawer__section" style="margin-top:14px;">
        <h4>${escapeHtml(t('admin.applications.section_reject'))}</h4>
        <div class="form-field form-field--required">
          <label for="pa-reject-reason">${escapeHtml(t('admin.applications.label_reason'))}</label>
          <textarea id="pa-reject-reason" rows="2" placeholder="${escapeHtml(t('admin.applications.reason_placeholder'))}"></textarea>
        </div>
      </div>
      <div id="pa-modal-error" class="modal__error" style="display:none;"></div>
    `;
  }

  function renderModalFoot(a: Application): string {
    if (a.status !== 'pending') {
      return `<button type="button" class="btn btn--ghost" id="pa-close-btn" style="padding:9px 14px;font-size:13px;">${escapeHtml(t('admin.actions.cancel'))}</button>`;
    }
    return `
      <button type="button" class="btn btn--ghost" id="pa-cancel-btn" style="padding:9px 14px;font-size:13px;">${escapeHtml(t('admin.actions.cancel'))}</button>
      <button type="button" class="btn btn--ghost" id="pa-reject-btn" style="padding:9px 14px;font-size:13px;color:var(--sk-status-occupied);border-color:rgba(182,67,58,0.3);">${escapeHtml(t('admin.applications.action_reject'))}</button>
      <button type="button" class="btn btn--primary" id="pa-approve-btn" style="padding:9px 14px;font-size:13px;">${escapeHtml(t('admin.applications.action_approve'))}</button>
    `;
  }

  function bindModalActions(a: Application): void {
    document.getElementById('pa-close-btn')?.addEventListener('click', closeModal);
    document.getElementById('pa-cancel-btn')?.addEventListener('click', closeModal);

    const approveBtn = document.getElementById('pa-approve-btn') as HTMLButtonElement | null;
    approveBtn?.addEventListener('click', async () => {
      const noteEl = document.getElementById('pa-approve-note') as HTMLTextAreaElement | null;
      setButtonBusy(approveBtn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/applications/${a.id}/approve`, {
          reviewNote: noteEl?.value.trim() || null,
        });
        closeModal();
        await load();
      } catch (err) {
        showModalError(modalErrorMessage(err, t('admin.applications.err_approve')));
      } finally {
        setButtonBusy(approveBtn, false);
      }
    });

    const rejectBtn = document.getElementById('pa-reject-btn') as HTMLButtonElement | null;
    rejectBtn?.addEventListener('click', async () => {
      const reasonEl = document.getElementById('pa-reject-reason') as HTMLTextAreaElement | null;
      const reason   = reasonEl?.value.trim() ?? '';
      if (!reason) {
        showModalError(t('admin.applications.err_reason_required'));
        return;
      }
      if (!(await confirmModal({
        title: t('admin.applications.action_reject'),
        message: t('admin.applications.confirm_reject'),
        confirmLabel: t('admin.applications.action_reject'),
      }))) return;
      setButtonBusy(rejectBtn, true);
      try {
        const client = await api();
        await client.post(`/api/admin/applications/${a.id}/reject`, { reason });
        closeModal();
        await load();
      } catch (err) {
        showModalError(modalErrorMessage(err, t('admin.applications.err_reject')));
      } finally {
        setButtonBusy(rejectBtn, false);
      }
    });
  }

  function modalErrorMessage(err: unknown, fallback: string): string {
    return (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      ?? (err instanceof Error ? err.message : fallback);
  }

  function showModalError(msg: string): void {
    const box = document.getElementById('pa-modal-error');
    if (!box) return;
    box.textContent = msg;
    box.style.display = 'block';
  }

  await load();
})();

const FMT_DATE = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
const FMT_DATETIME = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : FMT_DATE.format(d);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : FMT_DATETIME.format(d);
}
